import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, LogOut, Check, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEFAULT_MIN_LEN = 15;
const buildRules = (minLen) => [
  { key: "reqLength", test: (p) => p.length >= minLen },
  { key: "reqUpper", test: (p) => /[A-Z]/.test(p) },
  { key: "reqLower", test: (p) => /[a-z]/.test(p) },
  { key: "reqNumber", test: (p) => /[0-9]/.test(p) },
  { key: "reqSymbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

// Full-screen, non-dismissable gate shown when the signed-in console user still carries
// `mustChangePassword` (e.g. the seeded SaaS initial admin on first login). They cannot reach the
// app until they replace the initial password with one of their own.
export function ForcePasswordChange() {
  const { user, setUser, logout } = useAuth();
  const { t } = useLang();
  const minLen = Number(user?.passwordMinLength) || DEFAULT_MIN_LEN;
  const rules = buildRules(minLen);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error(t("forcePw.mismatch")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { currentPassword: pw.current, newPassword: pw.next });
      toast.success(t("forcePw.success"));
      setUser({ ...user, mustChangePassword: false });
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-5 py-10" data-testid="force-password-change">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-2.5">
          <img src="/logo.svg" alt="Scalio Nexus" className="h-9 w-9 shrink-0" />
          <div className="leading-none">
            <div className="font-display text-base font-black uppercase tracking-tight text-zinc-950">Scalio Nexus</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t("brand.subtitle")}</div>
          </div>
        </div>

        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-sm bg-indigo-50 text-indigo-600">
          <KeyRound size={24} strokeWidth={1.75} />
        </div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-zinc-950">{t("forcePw.title")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-zinc-600">{t("forcePw.subtitle")}</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("forcePw.current")}</Label>
            <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className="rounded-sm border-zinc-300" data-testid="force-pw-current" required autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("forcePw.new")}</Label>
            <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className="rounded-sm border-zinc-300" data-testid="force-pw-new" required />
            <PasswordStrength value={pw.next} t={t} rules={rules} minLen={minLen} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("forcePw.confirm")}</Label>
            <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="rounded-sm border-zinc-300" data-testid="force-pw-confirm" required />
          </div>
          <Button type="submit" disabled={saving || !rules.every((r) => r.test(pw.next))} data-testid="force-pw-submit"
            className="w-full rounded-sm bg-primary hover:bg-[#002480]">
            {saving ? t("forcePw.saving") : t("forcePw.submit")}
          </Button>
        </form>

        <button onClick={logout} data-testid="force-pw-logout"
          className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-700">
          <LogOut size={14} /> {t("forcePw.logout")}
        </button>
      </div>
    </div>
  );
}

function PasswordStrength({ value, t, rules, minLen }) {
  if (!value) return null;
  const passed = rules.filter((r) => r.test(value)).length;
  const pct = (passed / rules.length) * 100;
  const tone = passed <= 2 ? "weak" : passed === 3 ? "fair" : passed === 4 ? "good" : "strong";
  const barColor = { weak: "bg-red-500", fair: "bg-amber-500", good: "bg-lime-500", strong: "bg-emerald-600" }[tone];
  return (
    <div className="pt-1" data-testid="password-strength">
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-200">
          <div className={`h-full rounded-full transition-all duration-300 ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="w-14 shrink-0 text-right font-mono text-[10px] font-bold uppercase tracking-wider text-zinc-500"
          data-testid="password-strength-label">{t(`forcePw.${tone}`)}</span>
      </div>
      <ul className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
        {rules.map((r) => {
          const ok = r.test(value);
          return (
            <li key={r.key} className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-700" : "text-zinc-400"}`}
              data-testid={`pw-rule-${r.key}${ok ? "-ok" : ""}`}>
              {ok ? <Check size={12} className="shrink-0" /> : <X size={12} className="shrink-0" />}
              {t(`forcePw.${r.key}`).replace("{n}", minLen)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
